import React from 'react';

import { MRT_ColumnDef } from 'components/MRT';
import {
  FormattedAmount,
  InfoTooltip,
  DateLabel,
  IconTuple,
  PositionName,
  FallbackedTotalUSD,
  AddressIcon,
} from 'components';
import { getShortAddress } from 'utils/format';
import { colors, styled } from 'core/styles';
import { left } from 'utils/either';
import { riskFactorInfo } from 'domain/utils';

import { getRiskLevel } from '../../helpers';
import { RiskLevel } from '../../types';
import { Context, TableData } from './types';

const getFirstColumnByContext = (context: Context): MRT_ColumnDef<TableData> => {
  switch (context) {
    case 'market-positions':
      return {
        header: 'Account',
        id: 'name',
        minSize: 200,
        maxSize: 200,
        size: 200,
        enableColumnActions: false,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <AddressContainer>
            <AddressIcon address={row.original.account} fontSize="inherit" />
            <span>{getShortAddress(row.original.account, 5, 4)}</span>
          </AddressContainer>
        ),
      };
    case 'global-account-positions':
    case 'protocol-account-positions':
      return {
        header: 'Market name',
        id: 'name',
        size: 200,
        enableColumnActions: false,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <AddressContainer>
            <PositionName
              maxWidth={130}
              productSlug={row.original.productSlug}
              productName={row.original.productName}
              network={row.original.network}
              marketName={row.original.marketName}
            />
          </AddressContainer>
        ),
      };
  }
};

export const createColumns = (hideAmountColumns: boolean | undefined, context: Context) => {
  const lastActiveColumn: MRT_ColumnDef<TableData> = {
    header: 'Last active',
    id: 'lastActive',
    minSize: 120,
    maxSize: 230,
    size: hideAmountColumns ? 230 : 130,
    enableColumnActions: false,
    enableSorting: false,
    enableColumnFilter: false,
    Cell: ({ row }) => <DateLabel date={row.original.lastActive} />,
  };

  const assetsColumn: MRT_ColumnDef<TableData> = {
    header: 'Assets',
    id: 'assets',
    minSize: 65,
    maxSize: 100,
    size: 85,
    enableColumnActions: false,
    enableSorting: false,
    enableColumnFilter: false,
    Cell: ({ row }) => {
      return <IconTuple tokens={row.original.tokens} withTooltip />;
    },
    muiTableHeadCellProps: {
      align: 'left',
    },
  };

  const columns: MRT_ColumnDef<TableData>[] = hideAmountColumns
    ? [getFirstColumnByContext(context), lastActiveColumn]
    : [
        getFirstColumnByContext(context),
        {
          header: 'Deposits',
          id: 'deposits',
          minSize: 90,
          maxSize: 185,
          size: 130,
          enableColumnActions: false,
          enableSorting: false,
          enableColumnFilter: false,
          Cell: ({ row, table }) => (
            <FallbackedTotalUSD
              {...row.original.balances.deposit}
              formattedAmountProps={{ precision: 2 }}
              displayTokenAmounts={
                table.getState().displaySettings?.fallbackedTotalUSDVariant === 'fallback'
              }
              renderZero={() => <>–</>}
            />
          ),
        },
        {
          header: 'Collaterals',
          id: 'collaterals',
          minSize: 90,
          maxSize: 185,
          size: 130,
          enableColumnActions: false,
          enableSorting: false,
          enableColumnFilter: false,
          Cell: ({ row, table }) => (
            <FallbackedTotalUSD
              {...row.original.balances.collateral}
              formattedAmountProps={{ precision: 2 }}
              displayTokenAmounts={
                table.getState().displaySettings?.fallbackedTotalUSDVariant === 'fallback'
              }
              renderZero={() => <>–</>}
            />
          ),
        },
        {
          header: 'Borrows',
          id: 'borrows',
          minSize: 90,
          maxSize: 185,
          size: 130,
          enableColumnActions: false,
          enableSorting: false,
          enableColumnFilter: false,
          Cell: ({ row, table }) => (
            <FallbackedTotalUSD
              {...row.original.balances.debt}
              formattedAmountProps={{ precision: 2 }}
              displayTokenAmounts={
                table.getState().displaySettings?.fallbackedTotalUSDVariant === 'fallback'
              }
              renderZero={() => <>–</>}
            />
          ),
        },

        {
          header: 'Risk Factor',
          Header: () => (
            <HeaderBox>
              <span>Risk Factor</span>
              <InfoTooltip title={riskFactorInfo} />
            </HeaderBox>
          ),
          id: 'riskFactor',
          minSize: 110,
          maxSize: 110,
          size: 110,
          enableColumnActions: false,
          enableSorting: false,
          enableColumnFilter: false,
          Cell: ({ row }) => {
            return row.original.metrics.fold(
              metrics => {
                return metrics.riskFactor ? (
                  <RiskFactor riskLevel={getRiskLevel(metrics.riskFactor)}>
                    {Math.round(metrics.riskFactor * 100) / 100}
                  </RiskFactor>
                ) : (
                  '–'
                );
              },
              error => <FormattedAmount symbolSize="inherit" sum={left(error)} />,
            );
          },
        },
        lastActiveColumn,
        assetsColumn,
      ];
  return columns;
};

const AddressContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  margin: '-6px 0',

  [theme.breakpoints.up('desktopMD')]: {
    margin: '-4px 0',
  },
}));

const HeaderBox = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
});

const RiskFactor = styled('div', { shouldForwardProp: prop => prop !== 'riskLevel' })<{
  riskLevel: RiskLevel;
}>(({ riskLevel }) => ({
  color: {
    low: colors.greenShamrock,
    medium: colors.dolly,
    high: colors.scarlet[100],
  }[riskLevel],
}));
